import { getCorredores, createCorredor, updateCorredor, deleteCorredor } from '../models/corredorModel.js';
import Swal from 'sweetalert2';
import pb from '../lib/pocketbase.js';
import { toasts } from "svelte-toasts";

/**
 * Fetch corredores from the database with pagination.
 * @param {number} page - The page number.
 * @param {number} perPage - The number of items per page.
 * @returns {Object} - An object containing the corredores, total records, and current page.
 */
export async function fetchCorredores(page, perPage) {
  try {
    const response = await getCorredores(page, perPage);
    return {
      corredor: response.items,
      totalRecords: response.totalItems,
      currentPage: response.page,
    };
  } catch (error) {
    console.error("Error fetching corredores:", error);
    Swal.fire('Error', 'No se pudieron cargar los corredores', 'error');
    return null;
  }
}

/**
 * Save or update a corredor in the database.
 * @param {Object} corredor - The corredor object to save or update.
 * @param {string} currentCorredorId - The ID of the corredor to update (if applicable).
 */
export async function saveCorredor(corredor, currentCorredorId) {
  try {
    if (currentCorredorId) {
      await updateCorredor(currentCorredorId, corredor);
      toasts.add({
        title: 'Éxito',
        description: 'Corredor actualizado exitosamente',
        duration: 3000,
        placement: 'bottom-right',
        type: 'success',
        theme: 'dark',
        showProgress: true
      });
    } else {
      await createCorredor(corredor);
      toasts.add({
        title: 'Éxito',
        description: 'Corredor creado exitosamente',
        duration: 3000,
        placement: 'bottom-right',
        type: 'success',
        theme: 'dark',
        showProgress: true
      });
    }
  } catch (error) {
    console.error("Error saving corredor:", error);
    toasts.add({
      title: 'Error',
      description: 'Ocurrió un error al guardar el corredor',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true
    });
  }
}

/**
 * Remove a corredor from the database.
 * @param {string} id - The ID of the corredor to delete.
 */
export async function removeCorredor(id) {
  try {
    await deleteCorredor(id);
    toasts.add({
      title: 'Éxito',
      description: 'Corredor eliminado exitosamente.',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true
    });
  } catch (error) {
    console.error("Error deleting corredor:", error);
    Swal.fire('Error', 'No se pudo eliminar el corredor', 'error');
  }
}

/**
 * Subscribe to real-time changes in the 'corredor' collection.
 * @param {string} searchQuery - The search query (if applicable).
 * @param {Function} handleSearch - The function to call when a change occurs.
 */
export function subscribeToCorredores(searchQuery, handleSearch) {
  pb.collection('corredor').subscribe('*', async (e) => {
    console.log('Evento en tiempo real recibido:', e);
    await handleSearch(1); // Refresh the list with the first page
  });
}

/**
 * Unsubscribe from real-time changes in the 'corredor' collection.
 */
export function unsubscribeFromCorredores() {
  pb.collection('corredor').unsubscribe('*');
}

/**
 * Search for corredores based on a query.
 * @param {string} query - The search query.
 * @param {number} page - The page number.
 * @param {number} perPage - The number of items per page.
 * @returns {Object} - An object containing the corredores, total records, and current page.
 */
export async function searchCorredores(query, page = 1, perPage = 10) {
  const response = await pb.collection('corredor').getList(page, perPage, {
    filter: `nombre ~ "${query}"`,
  });
  return {
    corredor: response.items,
    totalRecords: response.totalItems,
    currentPage: response.currentPage,
  };
}
